import type { Options as HighchartsOptions } from 'highcharts';
import type { Margins, MultiVariableDataPoint, SimpleDataPoint } from './types';

interface CreateBarChartSeriesParams {
  data: SimpleDataPoint[] | MultiVariableDataPoint[];
  variables?: Record<string, string> | false | null | undefined;
  barsClasses: number[];
  name?: string;
}

export const createBarChartSeries = ({
  data,
  variables,
  barsClasses,
  name = '',
}: CreateBarChartSeriesParams): HighchartsOptions['series'] => {
  return variables
    ? Object.entries(variables).map(([label, field], i) => ({
        type: 'column' as const,
        name: label,
        data: data.map((dataPoint) => [
          new Date(dataPoint.date).getTime(),
          +(dataPoint.value as Record<string, number>)[field],
        ]),
        colorIndex: barsClasses[i],
      }))
    : [
        {
          type: 'column' as const,
          name: name,
          colorIndex: barsClasses[0],
          data: data.map((dataPoint) => [
            new Date(dataPoint.date).getTime(),
            +(dataPoint.value as number),
          ]),
        },
      ];
};

interface BuildBarChartOptionsParams {
  series: HighchartsOptions['series'];
  margins?: Margins;
  isAxesInvisible?: boolean;
  hourly?: boolean;
  unit?: string;
  useUTC?: boolean;
}

export const buildBarChartOptions = ({
  series,
  margins,
  isAxesInvisible = false,
  hourly = false,
  unit,
  useUTC = false,
}: BuildBarChartOptionsParams): HighchartsOptions => ({
  chart: {
    type: 'column',
    styledMode: true,
    marginTop: margins?.top,
    marginRight: margins?.right,
    marginBottom: margins?.bottom,
    marginLeft: margins?.left,
  },
  title: {
    text: '',
  },
  yAxis: {
    visible: !isAxesInvisible,
    title: {
      text: '',
    },
    softMax: 1,
  },
  xAxis: {
    visible: !isAxesInvisible,
    type: 'datetime',
    dateTimeLabelFormats: { day: '%e %b' },
    ...(hourly
      ? {
          units: [
            ['hour', [1]],
            ['day', null],
          ] as [string, number[] | null][],
          dateTimeLabelFormats: { hour: '%H', day: '%H' },
        }
      : {}),
  },
  legend: {
    enabled: false,
  },
  time: {
    useUTC,
  },
  credits: {
    enabled: false,
  },
  exporting: {
    enabled: false,
  },
  tooltip: {
    xDateFormat: hourly ? '%H' : '%A, %B %d',
    valueSuffix: unit ? ` ${unit}` : undefined,
    useHTML: true,
  },
  plotOptions: {
    column: {
      minPointLength: 3,
    },
  },
  series,
});
